import { forwardRef, useState, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, className = '', type, ...rest },
  ref
) {
  const [reveal, setReveal] = useState(false);
  const isPassword = type === 'password';
  return (
    <label className="flex flex-col gap-1.5 w-full">
      {label && <span className="text-sm font-medium text-black/70 dark:text-white/70">{label}</span>}
      <div className="relative">
        <input
          ref={ref}
          type={isPassword ? (reveal ? 'text' : 'password') : type}
          className={
            `w-full rounded-xl border px-3.5 py-2.5 text-[15px] bg-white dark:bg-white/5 outline-none transition-colors ${
              error ? 'border-red-500' : 'border-black/15 dark:border-white/15 focus:border-blue-500'
            } ${isPassword ? 'pr-10' : ''} ${className}`
          }
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? 'Hide value' : 'Show value'}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-black/40 dark:text-white/40 hover:text-black/70 dark:hover:text-white/70"
          >
            {reveal ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        )}
      </div>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </label>
  );
});

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export function TextArea({ label, error, className = '', ...rest }: TextAreaProps) {
  return (
    <label className="flex flex-col gap-1.5 w-full">
      {label && <span className="text-sm font-medium text-black/70 dark:text-white/70">{label}</span>}
      <textarea
        className={
          `w-full rounded-xl border px-3.5 py-2.5 text-[15px] bg-white dark:bg-white/5 outline-none transition-colors resize-y min-h-[100px] ${
            error ? 'border-red-500' : 'border-black/15 dark:border-white/15 focus:border-blue-500'
          } ${className}`
        }
        {...rest}
      />
      {error && <span className="text-xs text-red-500">{error}</span>}
    </label>
  );
}
